/**
 * T-125 (P0): all 37 of our ORIGINAL articles rendered "Unknown Source".
 *
 * The byline widgets derived their text from `post.source?.name`, and our
 * editorial bypasses ingestion, so those rows have `sourceId = null` and
 * therefore no joined `source` — while `post.author` holds the real writer's
 * name ("Rhea D'Souza", confirmed against production). We were erasing our own
 * writers from exactly the pages meant to establish our authority.
 *
 * THE DATA IS CORRECT; THE DISPLAY LOGIC WAS WRONG. We deliberately do NOT
 * backfill a synthetic source row onto our originals: "no source" is a
 * first-class kind of post (we wrote it), not a degraded aggregated one.
 *
 * Precedence: author first. For our originals it is the only truth available,
 * and for aggregated posts the human byline is the more informative label
 * anyway — a person, not a feed. Falls back to the source name, and only calls
 * something unattributed when BOTH are genuinely missing.
 */

export type BylineKind = 'author' | 'source' | 'unattributed';

export interface BylineSubject {
  author?: string | null;
  source?: { name?: string | null } | null;
}

export interface Byline {
  /** What to render. Never empty, never "Unknown Source" when a name exists. */
  name: string;
  /** Where the name came from — lets callers label or link appropriately. */
  kind: BylineKind;
  /** True when we have a real name to show. */
  hasName: boolean;
}

/**
 * The single rule every byline widget must use. Whitespace-only strings count
 * as missing: a name of " " renders as a blank byline, which reads as a bug.
 */
export function getByline(post: BylineSubject | null | undefined): Byline {
  const author = typeof post?.author === 'string' ? post.author.trim() : '';
  if (author) return { name: author, kind: 'author', hasName: true };

  const source = typeof post?.source?.name === 'string' ? post.source.name.trim() : '';
  if (source) return { name: source, kind: 'source', hasName: true };

  // Neutral, and deliberately not "Unknown Source": for our own writing there
  // is no source to be unknown about. This is the only case that should ever
  // render a placeholder at all.
  return { name: 'MotoWalla', kind: 'unattributed', hasName: false };
}

/** Convenience for the common "just give me the text" call site. */
export function bylineName(post: BylineSubject | null | undefined): string {
  return getByline(post).name;
}

/**
 * The label under the byline. An aggregated post is sourced from somewhere;
 * our own writing is not, so calling it a "Content Source" is wrong.
 */
export function bylineRole(post: BylineSubject | null | undefined): string {
  return getByline(post).kind === 'source' ? 'Content Source' : 'Writer';
}

/**
 * T-128: WHERE the byline should point, if anywhere.
 *
 * The feed cards used `post.source?.id ? '/source/'+id : '#'`. On our 37
 * originals sourceId is null, so they rendered a VISIBLE AFFORDANCE THAT GOES
 * NOWHERE: an `href="#"` looks clickable, takes the click, and does nothing.
 * Decision 105 — an option a user cannot complete must not be rendered.
 *
 * Precedence mirrors getByline(): if we are showing the AUTHOR's name, the link
 * must go to the AUTHOR's profile, never to the source of a different name.
 *
 * GATE ON THE RESOLVED USERNAME, NEVER ON THE DISPLAY STRING (T-130 rule).
 * `/user/${post.author}` would ship `/user/Formula 1` and, via `?.` inside a
 * template literal, the literal string `/user/undefined` — the dead link from
 * the T-125 tail. authorUsername is resolved server-side against the real users
 * table, so null genuinely means "no profile exists" and we render plain text.
 */
export interface BylineLinkSubject extends BylineSubject {
  authorUsername?: string | null;
  source?: { id?: string | null; name?: string | null } | null;
}

export function bylineLink(post: BylineLinkSubject | null | undefined): string | null {
  const byline = getByline(post);

  if (byline.kind === 'author') {
    const username = typeof post?.authorUsername === 'string' ? post.authorUsername.trim() : '';
    // No profile for this byline (every aggregated author, and any writer whose
    // row is missing) => unlinked text. A link to a profile that does not exist
    // is worse than no link.
    return username ? `/user/${username}` : null;
  }

  if (byline.kind === 'source') {
    const id = typeof post?.source?.id === 'string' ? post.source.id.trim() : '';
    return id ? `/source/${id}` : null;
  }

  return null;
}
